import { useCallback, useEffect, useRef, useState } from "react";

export type QuotaGroup = { new: number; renewal: number };
export type DashboardQuota = Record<"today" | "tomorrow" | "dayAfterTomorrow", QuotaGroup>;

export function parseDashboardQuota(data: unknown): DashboardQuota {
  if (!data || typeof data !== "object") throw new Error("Invalid quota response");
  const result = {} as DashboardQuota;
  for (const day of ["today", "tomorrow", "dayAfterTomorrow"] as const) {
    const group = (data as DashboardQuota)[day];
    if (!group || !Number.isInteger(group.new) || group.new < 0 ||
        !Number.isInteger(group.renewal) || group.renewal < 0) {
      throw new Error("Invalid quota count");
    }
    result[day] = { new: group.new, renewal: group.renewal };
  }
  return result;
}

let inFlight: Promise<DashboardQuota> | undefined;
function fetchQuota() {
  if (inFlight) return inFlight;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20000);
  inFlight = (async () => {
    const response = await fetch("/api/proxyparty_cnt", {
      credentials: "same-origin",
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) throw new Error("Quota request failed");
    return parseDashboardQuota(await response.json());
  })().finally(() => {
    window.clearTimeout(timeout);
    inFlight = undefined;
  });
  return inFlight;
}

export function useDashboardQuota() {
  const [data, setData] = useState<DashboardQuota | null>(null);
  const [error, setError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshRef = useRef<() => Promise<void>>(async () => {});
  const refresh = useCallback(() => refreshRef.current(), []);
  useEffect(() => {
    let disposed = false;
    let pending = false;
    const refresh = async () => {
      if (disposed || pending) return;
      pending = true;
      setIsRefreshing(true);
      try {
        const result = await fetchQuota();
        if (!disposed) {
          setData(result);
          setError(false);
        }
      } catch {
        if (!disposed) {
          setData(null);
          setError(true);
        }
      } finally {
        pending = false;
        if (!disposed) setIsRefreshing(false);
      }
    };
    refreshRef.current = refresh;
    refresh();
    const interval = window.setInterval(refresh, 5 * 60 * 1000);
    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, []);
  return { data, error, isRefreshing, refresh };
}
